import { Component, OnInit } from '@angular/core';
import { SpeechRecognitionService } from '../services/speech-recognition.service';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.css'],
})
export class ChatComponent implements OnInit {
  // Current transcript being built from live speech
  transcript: string = '';

  // Error message if speech recognition fails
  error: string = '';

  // History of completed transcripts (used for display/copy)
  allTranscripts: string[] = [];

  // Indicates whether microphone is actively listening
  isListening: boolean = false;

  // Holds the active subscription to speech recognition Observable
  private listeningSubscription: any;

  // Index of the transcript recently copied to clipboard (for feedback UI)
  copiedIndex: number | null = null;

  constructor(private speechService: SpeechRecognitionService) { }

  // Lifecycle hook, unused for now but required by interface
  ngOnInit() { }

  /**
   * Starts speech recognition if not already listening.
   * Subscribes to the speech recognition stream and updates transcript.
   */
  startListening() {
    // Prevent multiple starts
    if (this.isListening) return;

    // Reset values before starting a new session
    this.transcript = '';
    this.error = '';
    this.isListening = true;

    // Delay slightly to allow microphone permission dialog or engine warm-up
    setTimeout(() => {
      this.listeningSubscription = this.speechService.start().subscribe({

        // On receiving recognized text
        next: (text: string) => this.appendWithPunctuation(text),

        // On error (e.g., no-speech, aborted)
        error: (err) => {
          this.error = err;
          this.isListening = false;
        },

        // On completion (recognition session fully ended)
        complete: () => {
          this.isListening = false;
        }
      });
    }, 300); // 300ms delay helps with mic setup on some browsers
  }

  /**
   * Stops speech recognition and saves the current transcript.
   */
  stopListening() {
    // Do nothing if already not listening
    if (!this.isListening) return;

    // Stop recognition in service
    this.speechService.stop();
    this.isListening = false;

    // Unsubscribe from the Observable to clean up resources
    if (this.listeningSubscription) {
      this.listeningSubscription.unsubscribe();
      this.listeningSubscription = null;
    }

    // Save the current transcript if it has content
    if (this.transcript.trim()) {
      this.allTranscripts.push(this.transcript.trim());
      this.transcript = '';
    }
  }

  /**
   * Appends incoming text to the current transcript, inserting punctuation intelligently.
   */
  appendWithPunctuation(text: string) {
    // Remove leading/trailing spaces
    text = text.trim();

    // Add period if the current transcript doesn't end with any punctuation
    if (
      this.transcript &&
      !this.transcript.trim().endsWith('.') &&
      !this.transcript.trim().endsWith(',') &&
      !this.transcript.trim().endsWith('!')
    ) {
      this.transcript += '.';
    }

    // Add space between existing transcript and new text
    if (this.transcript) {
      this.transcript += ' ';
    }

    // Avoid double punctuation at the beginning of new input
    text = text.replace(/^[.,!?]+/, '');


    // Append cleaned text
    this.transcript += text;
  }

  /**
   * Copies a specific transcript to the clipboard and provides UI feedback.
   * @param text The transcript text to copy
   * @param index The index in the list for feedback indication
   */
  copyToClipboard(text: string, index: number) {
    navigator.clipboard.writeText(text).then(() => {
      // Mark which transcript was copied
      this.copiedIndex = index;

      // Clear the copied feedback after 2 seconds
      setTimeout(() => {
        this.copiedIndex = null;
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  }

  /**
   * Clears the buffer of saved transcripts.
   */
  clearBuffer() {
    this.allTranscripts = [];
  }
}
